/**
 * Reiter „Benachrichtigungen" der Konto-Seite (Spec 8.5).
 *
 * Laedt und speichert die Einstellungen des angemeldeten Mitglieds; die
 * Kaestchen-Matrix selbst steht in `NotificationPrefsForm`. Die Einstellungen
 * haengen am **Mitglied**, nicht am Konto — wer in zwei Mandanten arbeitet,
 * stellt sie je Mandant ein. Deshalb der Hinweis mit dem Mandantennamen.
 */
import type { NotificationPref } from '@tickethub/shared';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationPrefsForm } from '@/components/NotificationPrefsForm';
import { toastError } from '@/lib/errors';
import { useSession } from '@/lib/session';
import { notificationPrefsApi } from '@/lib/tickets';

export function AccountNotifications() {
  const { t } = useTranslation();
  const { activeMembership } = useSession();

  const tenantId = activeMembership?.tenant_id ?? null;
  const memberId = activeMembership?.member_id ?? null;
  const [prefs, setPrefs] = useState<NotificationPref[] | null>(null);

  const loadPrefs = useCallback(async () => {
    if (!tenantId || !memberId) return;
    try {
      setPrefs((await notificationPrefsApi.get(tenantId, memberId)).items);
    } catch {
      setPrefs([]);
    }
  }, [tenantId, memberId]);

  useEffect(() => {
    // `loadPrefs` setzt State erst *nach* dem Netzwerk-Await — es entsteht
    // keine kaskadierende Render-Schleife.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Daten beim Mount laden
    void loadPrefs();
  }, [loadPrefs]);

  if (!tenantId || !memberId) {
    return <p className="text-sm text-muted-foreground">{t('account.no_membership')}</p>;
  }

  return (
    <section className="max-w-xl space-y-3 rounded-lg border p-4">
      <div>
        <h2 className="text-lg font-semibold">{t('notification.title')}</h2>
        <p className="text-sm text-muted-foreground">{t('notification.intro')}</p>
        <p className="text-sm text-muted-foreground">
          {t('notification.per_tenant', { tenant: activeMembership?.tenant_name ?? '' })}
        </p>
      </div>

      {prefs === null ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <NotificationPrefsForm
          key={prefs.length}
          prefs={prefs}
          usingDefault={prefs.length === 0}
          onSave={async (next) => {
            try {
              const result = await notificationPrefsApi.save(tenantId, memberId, { prefs: next });
              setPrefs(result.items);
            } catch (error) {
              toastError(error, t);
            }
          }}
        />
      )}
    </section>
  );
}
